import { ReloadOutlined } from "@ant-design/icons";
import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type TouRow, useTou } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Button, Select, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

const measurementOptions = [
  { label: "A+", value: 1 },
  { label: "A-", value: 2 },
  { label: "R+", value: 3 },
  { label: "R-", value: 4 },
];

const defaultLimit = 5000;

const formatValue = (value?: number, digits = 3) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return value.toFixed(digits);
};

const ReportsTouPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [measurement, setMeasurement] = useState<number>(1);

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const touQuery = useTou({
    region: regionID,
    object: objectID,
    measurement,
    limit: defaultLimit,
  });

  const data = touQuery.data ?? [];
  const pagination = useStandardPagination(data.length);

  const columns: ColumnsType<TouRow> = [
    {
      title: "№ фидера",
      dataIndex: "feeder_id",
      width: 110,
    },
    {
      title: "Фидер",
      dataIndex: "feeder_name",
      ellipsis: true,
      render: (value: string, row) => row.feeder_dispatch ?? value,
    },
    {
      title: "Счётчик №",
      dataIndex: "meter_number",
      width: 140,
      render: (value?: number) => value ?? "—",
    },
    {
      title: "Название канала",
      dataIndex: "channel_label",
      ellipsis: true,
    },
    {
      title: "Измерение",
      dataIndex: "measurement_label",
      width: 120,
    },
    {
      title: "Дата чтения",
      dataIndex: "reading_time",
      width: 180,
      render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm"),
    },
    {
      title: "Тариф 1",
      dataIndex: "tariff1",
      width: 120,
      render: (value?: number) => formatValue(value),
    },
    {
      title: "Тариф 2",
      dataIndex: "tariff2",
      width: 120,
      render: (value?: number) => formatValue(value),
    },
    {
      title: "Тариф 3",
      dataIndex: "tariff3",
      width: 120,
      render: (value?: number) => formatValue(value),
    },
    {
      title: "Тариф 4",
      dataIndex: "tariff4",
      width: 120,
      render: (value?: number) => formatValue(value),
    },
    {
      title: "Итого",
      dataIndex: "total",
      width: 140,
      render: (value?: number) => formatValue(value),
    },
    {
      title: "Ct × Vt × M",
      dataIndex: "transformer_ratio",
      width: 140,
      render: (value: number) => formatValue(value, 4),
    },
  ];

  const ready = regionID !== undefined && objectID !== undefined;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/tou", {
        region: regionID,
        object: objectID,
        measurement,
        limit: defaultLimit,
      })
    : undefined;

  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const measurementLabel =
    measurementOptions.find((option) => option.value === measurement)?.label ?? `№${measurement}`;

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    openComposer({
      subject: `Текущие показания — ${objectLabel}`,
      body: `Отчёт: Текущие показания\nОбъект: ${objectLabel}\nСчётчики: все\nПоказатель: ${measurementLabel}\n\nExcel-файл находится во вложении.`,
      filename: `tou_${regionID ?? "r"}_${objectID ?? "obj"}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`,
    });
  }, [ready, currentObject, objectID, regionID, measurementLabel, openComposer]);

  return (
    <PageLayoutWithHelp
      title="Текущие показания"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow style={{ flexWrap: "nowrap" }}>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <Select
              options={measurementOptions}
              value={measurement}
              onChange={(value) => setMeasurement(value)}
              style={{ width: 160 }}
            />
            <Button
              aria-label="Обновить"
              title="Обновить"
              icon={<ReloadOutlined aria-hidden />}
              shape="circle"
              disabled={!ready}
              loading={touQuery.isFetching}
              onClick={() => touQuery.refetch()}
            />
          </FilterRow>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title="Выберите объект, чтобы загрузить текущие показания."
            style={{ maxWidth: 600 }}
          />
        )}
        {ready && (
          <EnterpriseTable<TouRow>
            rowKey={(row) =>
              `${row.feeder_id}-${row.meter_number ?? "meter"}-${row.measurement_type}-${row.reading_time}`
            }
            columns={columns}
            dataSource={data}
            loading={touQuery.isLoading}
            pagination={pagination}
            scroll={{ x: 1200 }}
            size="small"
          />
        )}
        {ready && !touQuery.isLoading && data.length === 0 && (
          <Alert
            type="warning"
            showIcon
            title="Нет текущих показаний"
            description="Для выбранного объекта нет записей в cnt.tou. Попробуйте выбрать другой объект."
            style={{ maxWidth: 800 }}
          />
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsTouPage;
